import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/admin/layout/Layout';
import Table from '../components/shared/Table';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Modal from '../components/shared/Modal';
import Badge from '../components/shared/Badge';
import { adminApi } from '../api/admin';
import type { User, UserRole, UserStatus, Project, Device } from '../types';
import type { ApiError } from '../api/client';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState<UserRole>('operator');
  const [formStatus, setFormStatus] = useState<UserStatus>('active');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formProjectIds, setFormProjectIds] = useState<string[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [formDeviceIds, setFormDeviceIds] = useState<string[]>([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.users.list();
      setUsers(data);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Users';
    return () => { document.title = 'VR Companion Admin'; };
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Load projects & devices once for checkbox lists
  useEffect(() => {
    adminApi.projects
      .list()
      .then((list) => {
        setProjects(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // non-fatal; project access checkboxes will just be empty
        setProjects([]);
      });

    adminApi.devices
      .list()
      .then((list) => {
        setDevices(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setDevices([]);
      });
  }, []);

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreate = () => {
    setEditingUser(null);
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('operator');
    setFormStatus('active');
    setFormProjectIds([]);
    setFormDeviceIds([]);
    setIsModalOpen(true);
  };

  const openEdit = (u: User) => {
    setEditingUser(u);
    setFormName(u.name);
    setFormEmail(u.email);
    setFormPassword('');
    setFormRole(u.role);
    setFormStatus(u.status);
    setFormProjectIds(u.projectIds ?? []);
    setFormDeviceIds(u.deviceIds ?? []);
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim() || !formEmail.trim()) return;
    setSubmitLoading(true);
    setError('');
    try {
      const payload = {
        name: formName.trim(),
        email: formEmail.trim(),
        role: formRole,
        status: formStatus,
        ...(formPassword.trim() && { password: formPassword }),
        // For admins, empty array = all projects/devices; for operators, explicit lists control access
        projectIds: formProjectIds,
        deviceIds: formDeviceIds,
      };
      if (editingUser) {
        await adminApi.users.update(editingUser.id, payload);
      } else {
        await adminApi.users.create(payload);
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (u: User) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    setError('');
    try {
      await adminApi.users.delete(u.id);
      fetchUsers();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete');
    }
  };

  const columns = [
    {
      header: 'Name',
      accessor: (row: User) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      ),
    },
    {
      header: 'Role',
      accessor: (row: User) => (
        <Badge variant={row.role === 'admin' ? 'info' : 'default'}>{row.role}</Badge>
      ),
    },
    {
      header: 'Status',
      accessor: (row: User) => (
        <Badge variant={row.status === 'active' ? 'success' : 'default'}>
          {row.status}
        </Badge>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: User) => (
        <div className="flex space-x-2">
          <button
            type="button"
            className="text-primary-600 hover:text-primary-900 text-sm font-medium"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="text-red-600 hover:text-red-900 text-sm font-medium"
            onClick={() => handleDelete(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout pageTitle="Users Management">
      <div className="space-y-6">
        {error && (
          <div className="error-alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div className="admin-toolbar">
          <div className="admin-toolbar-primary">
            <Input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="admin-toolbar-actions">
            <Button onClick={openCreate}>Add New User</Button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <Table columns={columns} data={filteredUsers} />
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={editingUser ? 'Edit User' : 'Add New User'}
          footer={
            <>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitLoading || !formName.trim() || !formEmail.trim()}
              >
                {submitLoading ? 'Saving...' : editingUser ? 'Update' : 'Create User'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Input
              label="Full Name"
              placeholder="John Doe"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <Input
              label="Email"
              type="email"
              placeholder="john.doe@example.com"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              disabled={!!editingUser}
            />
            <Input
              label={editingUser ? 'New password (leave blank to keep current)' : 'Password'}
              type="password"
              placeholder={editingUser ? '••••••••' : 'Set password for login'}
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={formRole}
                onChange={(e) => setFormRole(e.target.value as UserRole)}
              >
                <option value="admin">Admin</option>
                <option value="operator">Operator</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value as UserStatus)}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project access (for operators)
              </label>
              {projects.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No projects available yet. You can assign access later.
                </p>
              ) : (
                <div className="mt-1 max-h-48 overflow-auto border border-gray-200 rounded-lg px-3 py-2 space-y-1">
                  {projects.map((project) => {
                    const checked = formProjectIds.includes(project.id);
                    return (
                      <label
                        key={project.id}
                        className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={checked}
                          onChange={(e) => {
                            setFormProjectIds((prev) =>
                              e.target.checked
                                ? [...prev, project.id]
                                : prev.filter((id) => id !== project.id)
                            );
                          }}
                        />
                        <span className="truncate">{project.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                If left empty, operators will have access to all projects (same as admin).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Device access (optional)
              </label>
              {devices.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No devices registered yet. You can assign device access later.
                </p>
              ) : (
                <div className="mt-1 max-h-48 overflow-auto border border-gray-200 rounded-lg px-3 py-2 space-y-1">
                  {devices.map((device) => {
                    const checked = formDeviceIds.includes(device.id);
                    return (
                      <label
                        key={device.id}
                        className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={checked}
                          onChange={(e) => {
                            setFormDeviceIds((prev) =>
                              e.target.checked
                                ? [...prev, device.id]
                                : prev.filter((id) => id !== device.id)
                            );
                          }}
                        />
                        <span className="truncate">
                          {device.name} <span className="text-xs text-gray-400">({device.id})</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                If left empty, operators will see all devices for their allowed projects.
              </p>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default Users;
